import React, { useContext, useState } from "react";

import { MenuItem } from "@mui/material";
import { useQuery } from "@tanstack/react-query";
import { AppSwitch } from "Components/Generic/AppSwitch";
import { useTranslation } from "react-i18next";
import * as yup from "yup";

import { Select } from "../../../../../../../../../components/Form";
import RadioButton from "../../../../../../../../../components/Form/RadioGroup";
import SkeletonComponent from "../../../../../../../../../components/Generic/Skeleton";
import useYupValidation from "../../../../../../../../../hooks/useYupValidation";
import SubcontractorService from "../../../../../../../../../services/subcontractor.service";
import { MembersType } from "../../../../../../../../../util/enums";
import { TypeOfMember as MemberType } from "../../../../../../../../../util/enums/members";
import { SubcontractorKeys } from "../../../../../../../../../util/queries";
import { MemberContext } from "../../context";
import { AccordionCard } from "../Accordion";
import { CardComponent } from "../Card";
import useStyles, { CardsWrapper, SubcontractorWrapper, SwitchWRapper } from "./styles";

type Props = {
  errors?: yup.ValidationError[];
};

export default function TypeOfMember({ errors = [] }: Props) {
  const { t: translate } = useTranslation();
  const { member, setMember, handleUpdateMember } = useContext(MemberContext);
  const classes = useStyles();
  const { fieldHasError, getErrorByField } = useYupValidation(errors);

  const [hasSubcontractor, setHasSubcontractor] = useState<boolean>(false);

  const onChangeMemberType = (type: MemberType) => {
    setMember({
      ...member,
      typeOfMember: type,
      subcontractorId: undefined,
    });
  };

  const { data: subcontractorList, isFetching } = useQuery({
    queryKey: SubcontractorKeys.getAll([]),
    queryFn: async () => {
      const response = await SubcontractorService.getAll({
        companyId: member?.companyId,
        isActive: 1,
      });

      if (response?.data && Array.isArray(response.data)) return response.data;

      return [];
    },
    enabled: !!member?.companyId,
    refetchOnWindowFocus: false,
  });

  return (
    <React.Fragment>
      <CardsWrapper>
        <CardComponent.Root
          isActive={member?.typeOfMember?.toLowerCase() === MemberType.USER.toLowerCase()}
          onClick={() => onChangeMemberType(MemberType.USER)}
        >
          <CardComponent.Content>
            <RadioButton
              value={member?.typeOfMember}
              options={[
                {
                  label: "",
                  value: MemberType.USER,
                  asideChild: {
                    child: (
                      <CardComponent.Title
                        isActive={
                          member?.typeOfMember?.toLowerCase() === MemberType.USER.toLowerCase()
                        }
                      >
                        {translate("user")}
                      </CardComponent.Title>
                    ),
                    classnames: classes.radioLabel,
                  },
                },
              ]}
              onChange={(event) => onChangeMemberType(event.target.value as MemberType)}
            />
            <CardComponent.Subtitle>
              {translate("hasAccessToShipeeziSystemWithALoginAndPassword")}
            </CardComponent.Subtitle>
          </CardComponent.Content>
        </CardComponent.Root>
        <CardComponent.Root
          isActive={member?.typeOfMember?.toLowerCase() === MemberType.STAFF.toLowerCase()}
          onClick={() => onChangeMemberType(MemberType.STAFF)}
        >
          <CardComponent.Content>
            <RadioButton
              value={member?.typeOfMember}
              options={[
                {
                  label: "",
                  value: MembersType.STAFF,
                  asideChild: {
                    child: (
                      <CardComponent.Title
                        isActive={
                          member?.typeOfMember?.toLowerCase() === MemberType.STAFF.toLowerCase()
                        }
                      >
                        {translate("staff")}
                      </CardComponent.Title>
                    ),
                    classnames: classes.radioLabel,
                  },
                },
              ]}
              onChange={(event) => onChangeMemberType(event.target.value as MemberType)}
            />
            <CardComponent.Subtitle>
              {translate("anOperationalMemberUsedJustForRegisterPurposes")}
            </CardComponent.Subtitle>
          </CardComponent.Content>
        </CardComponent.Root>
      </CardsWrapper>
      {member?.typeOfMember?.trim() ? (
        <React.Fragment>
          <AccordionCard.Divider style={{ marginTop: "32px" }} />
          <SubcontractorWrapper>
            <SwitchWRapper>
              <AppSwitch
                checked={hasSubcontractor}
                onChange={(event: React.ChangeEvent<HTMLInputElement>) => {
                  setHasSubcontractor(event.target.checked);
                  if (!event.target.checked) handleUpdateMember("subcontractorId", null);
                }}
                label={translate("thisMemberBelongsToASubcontractor")}
              />
            </SwitchWRapper>
            {hasSubcontractor ? (
              <SkeletonComponent isLoading={isFetching}>
                <Select
                  label={translate("subcontractor")}
                  required
                  value={Number(member?.subcontractorId)}
                  onChange={(event) =>
                    handleUpdateMember("subcontractorId", Number(event.target.value))
                  }
                  error={fieldHasError("subcontractorId")}
                  errorMessage={getErrorByField("subcontractorId")}
                  placeholder={
                    subcontractorList?.length
                      ? translate("select")
                      : translate("noSubcontractorAvailable")
                  }
                >
                  {subcontractorList?.map((subcontractor) => {
                    return (
                      <MenuItem key={subcontractor.id} value={subcontractor.id}>
                        {subcontractor?.businessName}
                      </MenuItem>
                    );
                  })}
                </Select>
              </SkeletonComponent>
            ) : null}
          </SubcontractorWrapper>
        </React.Fragment>
      ) : null}
    </React.Fragment>
  );
}
